<template>
  <section class="quotewizard">
    <loader :loading="loading"></loader>
    <div class="container">
      <div class="quoteleft">
        <div v-if="createAccount" id="createaccount">
          <div class="title">Create Account</div>
          <br />
          <status-component></status-component>
          Register to save time in the future. Already have a NSSI account?
          <a class="actionlink" @click.prevent="createAccount = false">
            Login here.
          </a>
          <br /><br /><br />
          <div class="fieldleft">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('firstName') }"
            >
              First Name
              <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.firstName"
              type="text"
              name="firstName"
              :class="{ validationfield: hasErrors('firstName') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="firstName" :errors="validationErrors" />
          </div>
          <div class="fieldright">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('lastName') }"
            >
              Last Name <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.lastName"
              type="text"
              name="lastName"
              :class="{ validationfield: hasErrors('lastName') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="lastName" :errors="validationErrors" />
          </div>
          <div style="clear: both"></div>
          <div class="field">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('email') }"
            >
              Email <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.email"
              type="text"
              name="email"
              :class="{ validationfield: hasErrors('email') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="email" :errors="validationErrors" />
          </div>
          <div style="clear: both"></div>
          <br /><br />
          <div class="fieldleft">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('password') }"
            >
              Create Password <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.password"
              type="password"
              name="password"
              :class="{ validationfield: hasErrors('password') }"
              @input="validatedFieldChanged"
            />
            <hint v-pre>
              Minimum 6 characters: number(s), lowercase, uppercase, and
              character(s) required.
            </hint>
            <validation-error name="password" :errors="validationErrors" />
          </div>
          <div class="fieldright">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('confirmPassword') }"
            >
              Confirm Password <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.confirmPassword"
              type="password"
              name="confirmPassword"
              :class="{ validationfield: hasErrors('confirmPassword') }"
              @input="validatedFieldChanged"
            />
            <hint v-pre>
              Minimum 6 characters: number(s), lowercase, uppercase, and
              character(s) required.
            </hint>
            <validation-error
              name="confirmPassword"
              :errors="validationErrors"
            />
          </div>
        </div>
        <div v-else id="loginaccount">
          <div class="title">Login</div>
          <br />
          Log into your NSSI account.
          <a class="actionlink" @click.prevent="createAccount = true">
            Or create an account.
          </a>
          <br /><br />
          <status-component />
          <br />
          <div class="fieldleft">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('loginEmail') }"
            >
              Email Address <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.email"
              type="text"
              name="loginEmail"
              :class="{ validationfield: hasErrors('loginEmail') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="loginEmail" :errors="validationErrors" />
          </div>
          <div class="fieldright">
            <label
              class="tinylabel"
              :class="{ validationlabel: hasErrors('loginPassword') }"
            >
              Password <red v-pre>*</red>
            </label>
            <input
              v-model="currentUser.password"
              type="password"
              name="loginPassword"
              :class="{ validationfield: hasErrors('loginPassword') }"
              @input="validatedFieldChanged"
            />
            <validation-error name="loginPassword" :errors="validationErrors" />
          </div>
        </div>
        <div class="spacer"></div>
        <!--------------------------------------------------->
        <div style="text-align: center">
          <button
            :class="{ disabled: !validate }"
            @click="goToPage('quote-checkout')"
          >
            Continue
          </button>
        </div>
        <br /><br /><br /><br /><br /><br />
      </div>
      <quote-cart></quote-cart>
    </div>
  </section>
</template>

<script lang="ts" setup>
import QuoteCart from "@/views/quote/QuoteCart.vue";
import { User } from "@/models/User";
import { UserData } from "@/api/UserData";
import Loader from "@/components/MainLoader.vue";
import StatusComponent from "@/components/StatusComponent.vue";
import ValidationError from "@/components/ValidationError.vue";
import { onMounted, ref, computed, Ref, ComputedRef } from "vue";
import { useUserStore } from "@/stores/root/users";
import { useQuoteStore } from "@/stores/root/quote";
import { scrollMeTo, stat } from "@/utils/Utils";
import { useStatusStore } from "@/stores/root/status";

const createAccount: Ref<boolean> = ref(true);
const loading: Ref<boolean> = ref(false);
const userStore = useUserStore();
const quote = useQuoteStore();
const currentUser = ref(new User());
const validationErrors: Ref<any[]> = ref([]);
const user = userStore.user;
const statusStore = useStatusStore();

//we need token for reCaptcha on register
import { useReCaptcha } from "vue-recaptcha-v3";
const gReCaptcha = useReCaptcha();
const captchaToken = ref();
const insertRecaptchaToken = async () => {
  // (optional) Wait until recaptcha has been loaded.
  await gReCaptcha?.recaptchaLoaded();

  // Execute reCAPTCHA with action "login".
  captchaToken.value = await gReCaptcha?.executeRecaptcha("login");
  localStorage.setItem("rToken", captchaToken.value);
};

onMounted(async () => {
  if (user.loggedIn) {
    quote.setQuoteStep("quote-checkout");
    return;
  }
  loading.value = true;
  stat(statusStore).clear;
  loading.value = false;
});

function validatePasswordCriteria(): boolean {
  if (
    currentUser.value.password.match(
      /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{6,}$/
    )
  ) {
    return true;
  }
  return false;
}
const validate: ComputedRef<boolean> = computed(() => {
  if (createAccount.value) {
    return (
      currentUser.value.email.length > 0 &&
      currentUser.value.firstName.length > 0 &&
      currentUser.value.lastName.length > 0 &&
      currentUser.value.password.length > 0 &&
      currentUser.value.password == currentUser.value.confirmPassword &&
      validatePasswordCriteria()
    );
  } else {
    return (
      currentUser.value.email.length > 0 &&
      currentUser.value.password.length > 0
    );
  }
});
function createValidationMessages(): void {
  validationErrors.value.length = 0;
  if (createAccount.value) {
    if (!currentUser.value.email) {
      const error = {
        name: "email",
        value: "Email is required.",
      };
      validationErrors.value.push(error);
    }
    if (!currentUser.value.firstName) {
      const error = {
        name: "firstName",
        value: "First name is required.",
      };
      validationErrors.value.push(error);
    }
    if (!currentUser.value.lastName) {
      const error = {
        name: "lastName",
        value: "Last name is required.",
      };
      validationErrors.value.push(error);
    }
    const hasPassword = currentUser.value.password;
    if (!hasPassword) {
      const error = {
        name: "password",
        value: "Password is required.",
      };
      validationErrors.value.push(error);
    } else {
      if (!validatePasswordCriteria()) {
        const error = {
          name: "password",
          value:
            "Invalid password. Minimum 6 characters: number(s), lowercase, uppercase, and special character(s) required",
        };
        validationErrors.value.push(error);
      }
    }
    if (!currentUser.value.confirmPassword) {
      const error = {
        name: "confirmPassword",
        value: "Password confirmation is required.",
      };
      validationErrors.value.push(error);
    } else {
      if (currentUser.value.password != currentUser.value.confirmPassword) {
        const error = {
          name: "confirmPassword",
          value: "Password mismatch.",
        };
        validationErrors.value.push(error);
      }
    }
  } // login account
  else {
    if (!currentUser.value.email) {
      const error = {
        name: "loginEmail",
        value: "Email is required.",
      };
      validationErrors.value.push(error);
    }
    if (!currentUser.value.password) {
      const error = {
        name: "loginPassword",
        value: "Password is required.",
      };
      validationErrors.value.push(error);
    }
  }
}

async function goToPage(command: string) {
  stat(statusStore).clear;
  if (!validate.value) {
    createValidationMessages();
    stat(statusStore).error("Please fill in required fields.");
    scrollMeTo("status");
    return;
  }
  loading.value = true;
  let doLogin = false;

  if (createAccount.value) {
    await insertRecaptchaToken();
    const result = await UserData.createUser(
      currentUser.value.email,
      currentUser.value.password,
      currentUser.value.firstName,
      currentUser.value.lastName
    );
    if (result.error) {
      stat(statusStore).error(result.message);
      scrollMeTo("status");
      loading.value = false;
      return;
    } else {
      await UserData.login(currentUser.value.email, currentUser.value.password);
      doLogin = true;
    }
  } else {
    doLogin = true;
  }
  if (doLogin) {
    const loginResult = await UserData.login(
      currentUser.value.email,
      currentUser.value.password
    );
    if (loginResult.error) {
      stat(statusStore).error(loginResult.message);
      scrollMeTo("status");
      loading.value = false;
      return;
    }
  }
  loading.value = false;
  quote.setQuoteStep(command);
}
function clearValidationMessage(name: string): void {
  const elements = validationErrors.value.filter((element) => {
    return element.name == name;
  });
  elements.forEach((element) => {
    const index = validationErrors.value.indexOf(element);
    validationErrors.value.splice(index, 1);
  });
  if (!validationErrors.value.length) {
    stat(statusStore).clear;
  }
}
function validatedFieldChanged(event: any): void {
  clearValidationMessage(event.target.name);
}
function hasErrors(name: string): boolean {
  return (
    validationErrors.value.find((element) => {
      return element.name == name;
    }) != null
  );
}
</script>
